
import { DbProduct } from '@/hooks/useProduct';
import { ProductImageGallery } from './ProductImageGallery';
import { ProductInfo } from './ProductInfo';
import { ProductFeatures } from './ProductFeatures';
import { ProductAccordion } from './ProductAccordion';
import { RelatedProducts } from './RelatedProducts';
import { BundleIncludesSection } from './BundleIncludesSection';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';


interface ProductDetailLayoutProps {
  product: DbProduct;
  selectedColor: any;
  selectedVariant?: any;
  availableSizes: any[];
  availableColors: any[];
  onColorChange: (color: any) => void;
  onVariantChange?: (variant: any) => void;
  onCarouselNavigation: () => void;
  relatedProducts?: DbProduct[];
  isLoadingRelated: boolean;
  variants?: any[];
}

export const ProductDetailLayout = ({
  product,
  selectedColor,
  selectedVariant,
  availableSizes,
  availableColors,
  onColorChange,
  onVariantChange,
  onCarouselNavigation,
  relatedProducts,
  isLoadingRelated,
  variants = []
}: ProductDetailLayoutProps) => {
  const isMobile = useIsMobile();
  const [isDesktop, setIsDesktop] = useState(false);
  
  // Shared quantity state for mobile - lifted here so header and selectors share it
  const [mobileQuantity, setMobileQuantity] = useState(1);
  const handleMobileQuantityChange = useCallback((qty: number) => {
    setMobileQuantity(qty);
  }, []);

  // Load bundle children if this is a bundle product
  const [bundleItems, setBundleItems] = useState<{ id: string; name: string; imageUrl: string; quantity: number; urlSlug?: string }[]>([]);
  
  useEffect(() => {
    const loadBundle = async () => {
      if (!(product as any).is_bundle) return;
      const { data } = await supabase
        .from('product_bundles')
        .select('child_product_id, quantity')
        .eq('bundle_product_id', product.id)
        .order('sort_order');
      if (data && data.length > 0) {
        const childIds = data.map(d => d.child_product_id);
        
        // Fetch products and their variant images in parallel
        const [productsRes, variantImagesRes] = await Promise.all([
          supabase.from('products').select('id, name, image_urls, url_slug').in('id', childIds),
          supabase.from('variant_images').select('product_id, image_url').in('product_id', childIds).order('sort_order', { ascending: true }),
        ]);
        
        // Build representative image map from variant_images
        const repImageMap: Record<string, string> = {};
        if (variantImagesRes.data) {
          for (const vi of variantImagesRes.data) {
            if (!repImageMap[vi.product_id]) repImageMap[vi.product_id] = vi.image_url;
          }
        }
        
        setBundleItems(data.map(d => {
          const p = productsRes.data?.find(pr => pr.id === d.child_product_id);
          return {
            id: d.child_product_id,
            name: p?.name || '',
            imageUrl: p?.image_urls?.[0] || repImageMap[d.child_product_id] || '/placeholder.svg',
            quantity: d.quantity,
            urlSlug: p?.url_slug || undefined,
          };
        }));
      }
    };
    loadBundle();
  }, [product.id, (product as any).is_bundle]);

  // Desktop breakpoint at 768px (md: breakpoint) for sticky behavior
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    
    return () => {
      window.removeEventListener('resize', checkDesktop);
    };
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const leftWrapRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [isStickyReleased, setIsStickyReleased] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [stickyWidth, setStickyWidth] = useState<number | null>(null);
  const [stickyLeft, setStickyLeft] = useState<number | null>(null);

  // Compute stickyTop: header height + 16px margin
  const stickyTop = headerHeight + 16;

  // Calculate header height and sticky width (desktop only)
  useEffect(() => {
    if (!isDesktop) return;

    const calculateDimensions = () => {
      const header = document.querySelector('header') as HTMLElement;
      const measuredHeaderHeight = header ? header.offsetHeight : 0;
      setHeaderHeight(measuredHeaderHeight);
      
      // Recalculate sticky width and left position on resize
      if (leftWrapRef.current) {
        const rect = leftWrapRef.current.getBoundingClientRect();
        setStickyWidth(rect.width);
        setStickyLeft(rect.left);
      }
    };

    calculateDimensions();
    setTimeout(calculateDimensions, 100);
    
    window.addEventListener('resize', calculateDimensions);
    
    return () => {
      window.removeEventListener('resize', calculateDimensions);
    };
  }, [isDesktop]);

  // Scroll-based sticky release calculation (desktop only)
  // Use ref to track state without causing re-renders during scroll
  const stickyReleasedRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isDesktop || !mediaRef.current || !rightColumnRef.current || !containerRef.current) {
      return;
    }

    const updateStickyState = (shouldRelease: boolean) => {
      // Only update DOM/state if value changed
      if (stickyReleasedRef.current !== shouldRelease) {
        stickyReleasedRef.current = shouldRelease;
        setIsStickyReleased(shouldRelease);
      }
    };

    const calculateStickyState = () => {
      const leftHeight = mediaRef.current?.scrollHeight || 0;
      const rightHeight = rightColumnRef.current?.scrollHeight || 0;
      const viewportHeight = window.innerHeight;
      
      // Get the absolute position of container from top of page
      const containerOffsetTop = containerRef.current?.offsetTop || 0;
      const scrollTop = window.scrollY;
      const scrolledPastContainer = scrollTop - containerOffsetTop;
      
      // Calculate when left should start scrolling:
      // The left should release when the remaining scroll distance equals
      // the amount needed for both columns to reach their bottoms simultaneously
      const releasePoint = Math.max(0, rightHeight - viewportHeight - (leftHeight - viewportHeight + stickyTop));
      
      return scrolledPastContainer >= releasePoint;
    };

    const handleScroll = () => {
      // Cancel any pending frame to avoid stacking
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      
      // Use requestAnimationFrame for smooth updates synced to display refresh
      rafIdRef.current = requestAnimationFrame(() => {
        const shouldRelease = calculateStickyState();
        updateStickyState(shouldRelease);
        rafIdRef.current = null;
      });
    };

    // Initial check
    const initialState = calculateStickyState();
    updateStickyState(initialState);
    
    // Attach scroll listener with passive for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Watch for height changes in the right column (e.g., FAQ accordion opening)
    let resizeTimeout: NodeJS.Timeout;
    let savedScrollPosition: number | null = null;
    let isAnimating = false;
    
    const resizeObserver = new ResizeObserver(() => {
      // Capture scroll position only on first resize event (FAQ starts opening)
      if (savedScrollPosition === null && !isAnimating) {
        savedScrollPosition = window.scrollY;
        isAnimating = true;
      }
      
      // Restore scroll position to prevent jump
      if (savedScrollPosition !== null && isAnimating) {
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollPosition!);
        });
      }
      
      // Debounce the sticky recalculation
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const shouldRelease = calculateStickyState();
        updateStickyState(shouldRelease);
        savedScrollPosition = null;
        isAnimating = false;
      }, 250);
    });
    
    if (rightColumnRef.current) {
      resizeObserver.observe(rightColumnRef.current);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [isDesktop, relatedProducts, stickyTop]);


  // Compute primary image URLs for cart and pricing components (aligns with gallery priorities)
  const safeJsonParse = (data: any, defaultValue: any) => {
    if (!data) return defaultValue;
    if (typeof data === 'object') return data;
    try {
      return JSON.parse(data);
    } catch {
      return defaultValue;
    }
  };

  const colorImages = safeJsonParse((product as any).color_images, {});
  let computedImageUrls: string[] = [];

  if (selectedVariant?.image_url && typeof selectedVariant.image_url === 'string') {
    computedImageUrls = [selectedVariant.image_url];
  } else if (selectedColor?.id && colorImages[selectedColor.id]?.length) {
    computedImageUrls = (colorImages[selectedColor.id] as string[]).filter((u) => !!u && typeof u === 'string');
  } else if (Array.isArray(product.image_urls) && product.image_urls.length > 0) {
    computedImageUrls = product.image_urls.filter((u) => !!u && typeof u === 'string');
  } else {
    const allColorImages = Object.values(colorImages).flat() as string[];
    const generalImages = product.image_urls || [];
    computedImageUrls = [...new Set([...(generalImages as string[]), ...allColorImages])].filter((u) => !!u && typeof u === 'string');
  }

  return (
    <div className="bg-background min-h-screen">
      {/* Centered container with Easyplant-style spacing */}
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-20">
        <div className="max-w-[1280px] mx-auto">
          {/* Desktop Layout: Two columns with specific proportions */}
          <div className="hidden md:flex md:gap-6 lg:gap-10" ref={containerRef} style={{ overflowAnchor: 'none' }}>
            {/* Left side - Product Image + Related Products (55% tablet, 62% desktop) */}
            <div ref={leftWrapRef} className="md:w-[55%] lg:w-[62%] relative">
              {/* Sticky media wrapper - includes image gallery and related products */}
              <div 
                ref={mediaRef}
                className={`${isDesktop && !isStickyReleased ? 'product-media-sticky bg-background' : 'product-media-released'} z-30`}
                style={{
                  '--sticky-top': `${stickyTop}px`,
                  width: stickyWidth ? `${stickyWidth}px` : '100%',
                  left: !isStickyReleased && stickyLeft !== null ? `${stickyLeft}px` : 0
                } as React.CSSProperties}
              >
                <ProductImageGallery
                  product={product} 
                  selectedColor={selectedColor}
                  selectedVariant={selectedVariant}
                  aspectRatio="auto"
                  objectFit="contain"
                  onCarouselNavigation={onCarouselNavigation}
                />
                
                {/* Related Products - part of the sticky content */}
                <RelatedProducts 
                  relatedProducts={relatedProducts} 
                  isLoadingRelated={isLoadingRelated}
                  compact={true}
                />
              </div>
            </div>

            {/* Right side - Care section (45% tablet, 38% desktop) */}
            <div className="md:w-[45%] lg:w-[38%] pt-0" ref={rightColumnRef} style={{ overflowAnchor: 'none' }}>
              <div className="space-y-3 pb-12">
                <ProductInfo
                  product={product}
                  id={product.id}
                  name={product.name}
                  price={product.price}
                  description={product.description}
                  glanceInfo={product.glance_info}
                  stock={product.stock || 0}
                  availableSizes={availableSizes}
                  availableColors={availableColors}
                  defaultSize={product.default_size}
                  defaultColor={product.default_color}
                  onColorChange={onColorChange}
                  onVariantChange={onVariantChange}
                  discountPrice={product.discount_price}
                  isDiscounted={product.is_discounted}
                  selectedColor={selectedColor}
                  imageUrls={computedImageUrls}
                  dependencyRules={product.dependency_rules}
                  preloadedVariants={variants}
                  preloadedSelectedVariant={selectedVariant}
                />
                <ProductFeatures productAdvantages={(product as any).product_advantages} />
                <BundleIncludesSection items={bundleItems} />
                <ProductAccordion product={product} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Header - Outside normal flow to stay visible throughout page */}
      <div className="md:hidden fixed top-16 left-0 right-0 z-30">
        <ProductInfo 
          product={product}
          id={product.id}
          name={product.name}
          price={product.price}
          description={product.description}
          glanceInfo={product.glance_info}
          stock={product.stock || 0}
          availableSizes={availableSizes}
          availableColors={availableColors}
          defaultSize={product.default_size}
          defaultColor={product.default_color}
          onColorChange={onColorChange}
          onVariantChange={onVariantChange}
          discountPrice={product.discount_price}
          isDiscounted={product.is_discounted}
          selectedColor={selectedColor}
          imageUrls={computedImageUrls}
          dependencyRules={product.dependency_rules}
          isMobileLayout={true}
          mobileRenderMode="header"
          preloadedVariants={variants}
          preloadedSelectedVariant={selectedVariant}
          sharedQuantity={mobileQuantity}
          onSharedQuantityChange={handleMobileQuantityChange}
        />
        </div>

        {/* Mobile Layout: Image → Variants → Content */}
        <div className="md:hidden">
        
        {/* 2. Image Gallery - Edge to edge, no gap with sticky header */}
        <div>
          <ProductImageGallery
            product={product} 
            selectedColor={selectedColor}
            selectedVariant={selectedVariant}
            aspectRatio="auto"
            objectFit="contain"
            onCarouselNavigation={onCarouselNavigation}
          />
        </div>
        
        {/* 3. Variant Selectors */}
        <div className="mt-6">
          <ProductInfo 
            product={product}
            id={product.id}
            name={product.name}
            price={product.price}
            description={product.description}
            glanceInfo={product.glance_info}
            stock={product.stock || 0}
            availableSizes={availableSizes}
            availableColors={availableColors}
            defaultSize={product.default_size}
            defaultColor={product.default_color}
            onColorChange={onColorChange}
            onVariantChange={onVariantChange}
            discountPrice={product.discount_price}
            isDiscounted={product.is_discounted}
            selectedColor={selectedColor}
            imageUrls={computedImageUrls}
            dependencyRules={product.dependency_rules}
            isMobileLayout={true}
            mobileRenderMode="selectors"
            preloadedVariants={variants}
            preloadedSelectedVariant={selectedVariant}
            sharedQuantity={mobileQuantity}
            onSharedQuantityChange={handleMobileQuantityChange}
          />
        </div>
        
        <div className="mt-6 space-y-6 px-4">
          <ProductFeatures productAdvantages={(product as any).product_advantages} />
          <BundleIncludesSection items={bundleItems} />
          <ProductAccordion product={product} />
        </div>
        
        {/* Mobile Related Products */}
        <div className="mt-2 md:mt-6 px-4">
          <RelatedProducts
            relatedProducts={relatedProducts} 
            isLoadingRelated={isLoadingRelated} 
          />
        </div>
      </div>
    </div>
  );
};
